import React, { useState, useEffect, useRef } from 'react';
import { redirectToCheckout, PRICE_IDS } from '../services/stripe';
import './Pricing.css';

const CheckIcon = ({ tier }) => {
  if (tier === 'free') {
    return (
      <svg className="check-icon check-icon--free" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="8" stroke="#9CA3AF" strokeWidth="1.5" />
        <path d="M5.5 9l2.5 2.5 4.5-4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (tier === 'pro') {
    return (
      <svg className="check-icon check-icon--pro" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="9" fill="#2563EB" />
        <path d="M5.5 9l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg className="check-icon check-icon--premium" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="9" fill="#111827" />
      <path d="M5.5 9l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const NotIncludedIcon = () => (
  <svg className="check-icon check-icon--none" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="8" stroke="#E5E7EB" strokeWidth="1.2" />
    <path d="M6.5 9h5" stroke="#E5E7EB" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, flexShrink: 0 }}>
    <rect x="3" y="6" width="8" height="6" rx="1.5" stroke="#9CA3AF" strokeWidth="1.5" />
    <path d="M5 6V4.5a2 2 0 014 0V6" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: 6, verticalAlign: -3, flexShrink: 0 }}>
    <path d="M8 1L2.5 3.5V7C2.5 10.5 5 13.5 8 14.5C11 13.5 13.5 10.5 13.5 7V3.5L8 1Z" stroke="#2563EB" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M5.5 8l2 2 3-3.5" stroke="#2563EB" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const plans = [
  {
    tier: 'free',
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: '',
    cta: 'Get Started',
    features: [
      { text: '3 apps/day', included: true },
      { text: '1 resume', included: true },
      { text: 'Automated cold emails', included: false },
      { text: 'Basic stats', included: true },
      { text: 'Community support', included: true },
      { text: 'Cover letters & AI tools', included: false },
    ],
  },
  {
    tier: 'pro',
    name: 'Pro',
    monthlyPrice: 10,
    yearlyPrice: 8,
    yearlyTotal: 96,
    description: '',
    cta: 'Get Started',
    features: [
      { text: '10 apps/day', included: true },
      { text: '3 resumes', included: true },
      { text: 'Automated cold emails', included: true, highlight: true },
      { text: 'Full analytics dashboard', included: true },
      { text: 'Priority support', included: true },
      { text: 'Custom cover letters', included: true },
    ],
  },
  {
    tier: 'premium',
    name: 'Premium',
    monthlyPrice: 20,
    yearlyPrice: 16,
    yearlyTotal: 192,
    description: '',
    cta: 'Get Started',
    features: [
      { text: '30 apps/day', included: true },
      { text: '10 resumes', included: true },
      { text: 'Automated cold emails', included: true },
      { text: 'Full analytics + export', included: true },
      { text: '1-on-1 onboarding', included: true },
      { text: 'Cover letters + AI resume tailoring', included: true },
    ],
  },
];

const PricingCard = ({ plan, billingCycle, priceSwitching, isVisible, onCtaClick, checkoutLoading }) => {
  const isPro = plan.tier === 'pro';
  const isPremium = plan.tier === 'premium';
  const isYearly = billingCycle === 'yearly';
  const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  const originalPrice = plan.monthlyPrice;

  return (
    <div
      className={`pricing-card ${isPro ? 'pricing-card--pro' : ''} ${
        isPremium ? 'pricing-card--premium' : ''
      } ${isVisible ? 'pricing-card--visible' : ''}`}
      style={{ animationDelay: plan.tier === 'free' ? '0ms' : plan.tier === 'pro' ? '100ms' : '200ms' }}
    >
      {isPro && (
        <span className={`popular-badge ${isVisible ? 'popular-badge--visible' : ''}`}>
          <span className="popular-badge__sparkle">&#10022;</span> Most Popular
        </span>
      )}

      <div className="card-content">
        <h3 className={`tier-name ${isPro ? 'tier-name--pro' : ''}`}>{plan.name}</h3>

        <div className={`price-block ${priceSwitching ? 'price-block--switching' : ''}`}>
          {isYearly && plan.monthlyPrice > 0 && (
            <span className="price-original">${originalPrice}</span>
          )}
          <span className={`price-dollar ${isPro ? 'price-dollar--pro' : ''}`}>$</span>
          <span className={`price-amount ${isPro ? 'price-amount--pro' : ''}`}>{price}</span>
          <span className="price-suffix">/ mo</span>
        </div>
        {isYearly && plan.yearlyTotal && (
          <div className="price-annual-note">billed as ${plan.yearlyTotal}/year</div>
        )}

        {plan.description && (
          <p className={`tier-description ${isPro ? 'tier-description--pro' : ''}`}>
            {plan.description}
          </p>
        )}

        <div className={`card-divider ${isPro ? 'card-divider--pro' : ''}`} />

        <ul className="feature-list">
          {plan.features.map((feature, i) => (
            <li key={i} className={`feature-row ${!feature.included ? 'feature-row--disabled' : ''}`}>
              {feature.included ? (
                <CheckIcon tier={plan.tier} />
              ) : (
                <NotIncludedIcon />
              )}
              <span
                className={`feature-text ${feature.highlight ? 'feature-text--highlight' : ''} ${
                  !feature.included ? 'feature-text--disabled' : ''
                }`}
              >
                {feature.text}
                {feature.highlight && <span className="feature-star">&#9733;</span>}
              </span>
            </li>
          ))}
        </ul>

        <a
          href={plan.tier === 'free' ? '/signup' : '/signup'}
          className={`cta-btn ${
            isPro ? 'cta-btn--pro' : isPremium ? 'cta-btn--premium' : 'cta-btn--free'
          }`}
          onClick={(e) => {
            if (plan.tier !== 'free') {
              e.preventDefault();
              if (!checkoutLoading) onCtaClick(plan.tier, billingCycle);
            }
          }}
          style={checkoutLoading === plan.tier ? { opacity: 0.7, pointerEvents: 'none' } : {}}
        >
          {checkoutLoading === plan.tier ? 'Redirecting...' : plan.cta}
        </a>
        {isPro && (
          <div className="cta-reassurance">
            <LockIcon />
            No credit card required
          </div>
        )}
        {isPremium && (
          <div className="cta-reassurance">
            Cancel anytime
          </div>
        )}
      </div>
    </div>
  );
};

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [priceSwitching, setPriceSwitching] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');
  const sectionRef = useRef(null);

  const handleCtaClick = async (tier, cycle) => {
    const isYearly = cycle === 'yearly';
    let priceId;
    if (tier === 'pro') {
      priceId = isYearly ? PRICE_IDS.pro_yearly : PRICE_IDS.pro_monthly;
    } else if (tier === 'premium') {
      priceId = isYearly ? PRICE_IDS.premium_yearly : PRICE_IDS.premium_monthly;
    }
    if (!priceId) return;

    setCheckoutLoading(tier);
    setCheckoutError('');
    const result = await redirectToCheckout(priceId);
    if (result && !result.success) {
      setCheckoutLoading(null);
      if (result.error === 'network') {
        setCheckoutError('Could not connect to the server. Please try again.');
      } else if (result.error !== 'not_authenticated') {
        setCheckoutError('Something went wrong. Please try again.');
      }
      setTimeout(() => setCheckoutError(''), 5000);
    }
  };

  const handleToggle = (cycle) => {
    if (cycle === billingCycle) return;
    setPriceSwitching(true);
    setTimeout(() => {
      setBillingCycle(cycle);
      setPriceSwitching(false);
    }, 250);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCardsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pricing-page">
      <section className="pricing-section" ref={sectionRef}>
        <div className="pricing-container">
          <div className="pricing-header">
            <span className="pricing-kicker">Choose your plan</span>
            <h1 className="pricing-title">Apply on autopilot. Pick your speed.</h1>
            <p className="pricing-subtitle">
              Every plan includes automated applications and cold recruiter outreach. Upgrade or downgrade anytime.
            </p>

            <div className="billing-toggle-wrapper">
              <span className="savings-badge">
                Save 20%
                <span className="savings-badge__arrow" />
              </span>
              <div className="billing-toggle">
                <div
                  className="billing-toggle__slider"
                  style={{
                    transform:
                      billingCycle === 'yearly'
                        ? 'translateX(100%)'
                        : 'translateX(0%)',
                  }}
                />
                <button
                  className={`billing-toggle__option ${billingCycle === 'monthly' ? 'billing-toggle__option--active' : ''}`}
                  onClick={() => handleToggle('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={`billing-toggle__option ${billingCycle === 'yearly' ? 'billing-toggle__option--active' : ''}`}
                  onClick={() => handleToggle('yearly')}
                >
                  Yearly
                </button>
              </div>
            </div>
          </div>

          {checkoutError && (
            <div className="pricing-error">
              {checkoutError}
            </div>
          )}

          <div className="pricing-grid">
            {plans.map((plan) => (
              <PricingCard
                key={plan.tier}
                plan={plan}
                billingCycle={billingCycle}
                priceSwitching={priceSwitching}
                isVisible={cardsVisible}
                onCtaClick={handleCtaClick}
                checkoutLoading={checkoutLoading}
              />
            ))}
          </div>

          <div className="pricing-guarantee">
            <div className="pricing-guarantee__pill">
              <ShieldIcon />
              All plans include a 14-day money-back guarantee.
            </div>
            <a href="/" onClick={(e) => e.preventDefault()} className="pricing-contact-link">
              Questions? Talk to a human &rarr;
            </a>
          </div>
        </div>
      </section>


    </div>
  );
};

export default Pricing;
